import "./login.css";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import RareBoxLogo from "../../../Logos/RareBoxLogo";
import { useEffect, useState } from "react";
import { GlobalState } from "../../../context/GlobalContext";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ForgotPasswordModal from "./ForgotPasswordModal/ForgotPasswordModal";

const Login = () => {
	const { snackBar, setSnackBar, setSnackBarOpen, setLoginUser, setLoadingOpen } = GlobalState();
	const [open, setOpen] = useState(false);
	const navigate = useNavigate();
	const [step, setStep] = useState("login");
	const [challengeId, setChallengeId] = useState("");
	const [otp, setOtp] = useState("");
	const [expiresAt, setExpiresAt] = useState("");

	useEffect(() => {
		if (localStorage.getItem("user_token") && localStorage.getItem("py_token")) navigate("/explore");
	}, []);

	const [userName, setUserName] = useState("");
	const [password, setPassword] = useState("");

	const showSnackBarMessage = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({ ...snackBar, message, severity });
	};

	const resetOtpStep = () => {
		setStep("login");
		setChallengeId("");
		setOtp("");
		setExpiresAt("");
	};

	const handleLoginClick = async (e) => {
		e.preventDefault();
		setLoadingOpen(true);
		if (userName === "" || password === "") {
			showSnackBarMessage("Enter both email and password");
			setLoadingOpen(false);
			return;
		}

		try {
			const res = await axios.post("/api/auth2/login", { email: userName, password });
			setChallengeId(res.data.challengeId);
			setExpiresAt(res.data.expiresAt || "");
			setOtp("");
			setStep("otp");
			showSnackBarMessage(res.data.message || "OTP sent to your email", "success");
		} catch (error) {
			const response = error.response;
			if (response && response.data && response.data.status === "error")
				showSnackBarMessage(response.data.error || "Unable to start login");
			else showSnackBarMessage("Login failed Server Error");
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleVerifyOtp = async (e) => {
		e.preventDefault();
		setLoadingOpen(true);

		if (otp.trim() === "") {
			showSnackBarMessage("Enter the OTP sent to your email");
			setLoadingOpen(false);
			return;
		}

		try {
			const res = await axios.post("/api/auth2/verify_otp", {
				challengeId,
				otp: otp.trim(),
			});
			const userdata = res.data.user;
			const pyToken = res.data.pyToken;
			setLoginUser(userdata);
			localStorage.setItem("user_token", userdata.token);
			if (pyToken) localStorage.setItem("py_token", pyToken);
			navigate("/explore");
		} catch (error) {
			const response = error.response;
			if (response && response.data && response.data.status === "error")
				showSnackBarMessage(response.data.error || "OTP verification failed");
			else showSnackBarMessage("OTP verification failed");
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleResendOtp = async () => {
		setLoadingOpen(true);

		try {
			const res = await axios.post("/api/auth2/resend_otp", {
				challengeId,
			});
			setChallengeId(res.data.challengeId || challengeId);
			setExpiresAt(res.data.expiresAt || "");
			setOtp("");
			showSnackBarMessage(res.data.message || "OTP resent to your email", "success");
		} catch (error) {
			const response = error.response;
			if (response && response.data && response.data.status === "error")
				showSnackBarMessage(response.data.error || "Unable to resend OTP");
			else showSnackBarMessage("Unable to resend OTP");
		} finally {
			setLoadingOpen(false);
		}
	};

	const expiryLabel = expiresAt ? new Date(expiresAt).toLocaleString() : "";

	return (
		<Box
			sx={{
				minHeight: "100dvh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				px: { xs: 2, sm: 3, md: 5 },
				py: { xs: 2, md: 3 },
				boxSizing: "border-box",
				background:
					"radial-gradient(circle at top left, rgba(255,255,255,0.96), rgba(240,238,233,0.92) 42%, rgba(229,226,220,0.9) 100%)",
			}}>
			<Box
				sx={{
					width: "100%",
					maxWidth: 1180,
					display: "grid",
					gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
					borderRadius: "10px",
					overflow: "hidden",
					border: "1px solid rgba(17, 17, 17, 0.08)",
					boxShadow: "0 24px 80px rgba(34, 34, 34, 0.12)",
					backgroundColor: "rgba(255,255,255,0.72)",
					backdropFilter: "blur(18px)",
				}}>
				<Box
					sx={{
						display: { xs: "none", md: "flex" },
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						gap: 3,
						p: { md: 5, lg: 7 },
						background:
							"linear-gradient(145deg, rgba(255,255,255,0.78) 0%, rgba(247,244,239,0.94) 48%, rgba(231,226,217,0.98) 100%)",
						borderRight: "1px solid rgba(17, 17, 17, 0.08)",
					}}>
					<Box
						sx={{
							width: "100%",
							maxWidth: 400,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							"& svg": {
								display: "block",
								width: "100%",
								height: "auto",
							},
						}}>
						<RareBoxLogo />
					</Box>
					<Box
						sx={{
							display: "inline-flex",
							px: 2,
							py: 1,
							borderRadius: "999px",
							backgroundColor: "rgba(17, 17, 17, 0.06)",
							color: "var(--color-text-secondary)",
							fontSize: "0.8rem",
							fontWeight: 600,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}>
						Secure Access
					</Box>
					<Stack spacing={1.5} sx={{ maxWidth: 440 }}>
						<Typography
							sx={{
								fontSize: "1.05rem",
								fontWeight: 600,
								letterSpacing: "-0.02em",
								color: "var(--color-text-primary)",
								textAlign: "center",
							}}>
							The internal operations platform for thehouseofrare.com
						</Typography>
						<Typography
							sx={{
								fontSize: "0.92rem",
								lineHeight: 1.75,
								color: "var(--color-text-secondary)",
								textAlign: "center",
							}}>
							Business tools our team uses to run day-to-day operations. Authorised employees only.
						</Typography>
					</Stack>
				</Box>
				<Stack
					component="form"
					onSubmit={step === "login" ? handleLoginClick : handleVerifyOtp}
					spacing={2.5}
					sx={{
						p: { xs: 3, sm: 4, md: 5 },
						backgroundColor: "rgba(255,255,255,0.82)",
						minHeight: { md: 640 },
						justifyContent: "center",
						alignSelf: "stretch",
					}}>
					<Box
						sx={{
							width: { xs: 180, sm: 210 },
							display: { xs: "block", md: "none" },
							alignSelf: "center",
							mb: 1,
							"& svg": {
								display: "block",
								width: "100%",
								height: "auto",
							},
						}}>
						<RareBoxLogo />
					</Box>
					<Stack spacing={1} alignItems={{ xs: "flex-start", md: "flex-start" }}>
						<Box
							sx={{
								px: 1.5,
								py: 0.75,
								borderRadius: "999px",
								backgroundColor: "rgba(17, 17, 17, 0.06)",
								color: "var(--color-text-secondary)",
								fontSize: "0.78rem",
								fontWeight: 600,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
							}}>
							{step === "login" ? "Step 1 of 2" : "Step 2 of 2"}
						</Box>
						<Typography
							sx={{
								fontSize: { xs: "1.9rem", sm: "2.25rem" },
								lineHeight: 1.05,
								fontWeight: 600,
								letterSpacing: "-0.04em",
								color: "var(--color-text-primary)",
							}}>
							{step === "login" ? "Sign in" : "Verify"}
						</Typography>
					</Stack>
					{step === "login" ? (
						<>
							<TextField
								onChange={(e) => setUserName(e.target.value)}
								value={userName}
								// size="small"
								id="login-email"
								label="Email"
								type="text"
								autoComplete=""
								fullWidth
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: "5px",
										backgroundColor: "rgba(255,255,255,0.88)",
									},
								}}
							/>
							<TextField
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								// size="small"
								id="login-password"
								label="Password"
								type="password"
								autoComplete=""
								fullWidth
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: "5px",
										backgroundColor: "rgba(255,255,255,0.88)",
									},
								}}
							/>
							<Button
								variant="contained"
								fullWidth
								type="submit"
								sx={{
									minHeight: 50,
									borderRadius: "5px",
									textTransform: "none",
									fontSize: "1rem",
									fontWeight: 600,
									backgroundColor: "#111111",
									boxShadow: "none",
									"&:hover": {
										backgroundColor: "#222222",
										boxShadow: "none",
									},
								}}>
								Continue
							</Button>
							<Typography sx={{ color: "var(--color-text-secondary)" }}>
								Forgot Password ?{" "}
								<span
									style={{ color: "#111111", cursor: "pointer", fontWeight: 600, textDecoration: "underline" }}
									onClick={() => setOpen(true)}>
									Click Here
								</span>
							</Typography>
						</>
					) : (
						<>
							<Typography sx={{ color: "var(--color-text-secondary)", lineHeight: 1.7 }}>
								Enter the OTP sent to {userName}.
							</Typography>
							{expiryLabel ? (
								<Typography variant="body2" sx={{ color: "var(--color-text-light)" }}>
									OTP expires at {expiryLabel}
								</Typography>
							) : null}
							<TextField
								value={otp}
								onChange={(e) => setOtp(e.target.value)}
								// borderRadius: "5px"
								id="login-otp"
								label="OTP"
								type="text"
								autoComplete="one-time-code"
								fullWidth
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: "5px",
										backgroundColor: "rgba(255,255,255,0.88)",
									},
								}}
							/>
							<Button
								variant="contained"
								fullWidth
								type="submit"
								sx={{
									minHeight: 50,
									borderRadius: "5px",
									textTransform: "none",
									fontSize: "1rem",
									fontWeight: 600,
									backgroundColor: "#111111",
									boxShadow: "none",
									"&:hover": {
										backgroundColor: "#222222",
										boxShadow: "none",
									},
								}}>
								Verify OTP
							</Button>
							<Button
								variant="outlined"
								fullWidth
								type="button"
								onClick={handleResendOtp}
								sx={{
									minHeight: 48,
									borderRadius: "5px",
									textTransform: "none",
									fontWeight: 600,
									borderColor: "rgba(17, 17, 17, 0.18)",
									color: "#111111",
								}}>
								Resend OTP
							</Button>
							<Button
								variant="text"
								fullWidth
								type="button"
								onClick={resetOtpStep}
								sx={{
									minHeight: 44,
									borderRadius: "5px",
									textTransform: "none",
									fontWeight: 600,
									color: "var(--color-text-secondary)",
								}}>
								Back to login
							</Button>
						</>
					)}
					<Divider sx={{ borderColor: "rgba(17, 17, 17, 0.08)" }} />
					<Typography
						sx={{
							display: { xs: "block", md: "none" },
							fontSize: "0.86rem",
							lineHeight: 1.7,
							color: "var(--color-text-secondary)",
						}}>
						Rarebox is the internal platform for thehouseofrare.com — business tools our team uses to run
						day-to-day operations. Authorised employees only.
					</Typography>
					<Typography
						sx={{
							fontSize: "0.84rem",
							color: "var(--color-text-light)",
							"& a": { color: "inherit", fontWeight: 600, marginRight: 2 },
						}}>
						<Link to="/privacy-policy">Privacy Policy</Link>
						<Link to="/terms-of-service">Terms of Service</Link>
					</Typography>
				</Stack>
			</Box>
			<ForgotPasswordModal open={open} setOpen={setOpen} />
		</Box>
	);
};

export default Login;
